"use client"

import { useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"

// Site key is public — it's the matching Secret key (server-only, in Secrets
// Manager as TURNSTILE_SECRET_KEY) that proves server-side identity.
const TURNSTILE_SITE_KEY = "0x4AAAAAAD8ELWLWdneutL1m"

export type ConsultationFormVariant = "default" | "hero"

export interface ConsultationFormProps {
  /**
   * Visual styling:
   * - "hero" — owns its own warm-cream Card wrapper (used on the homepage hero band on a dark background).
   * - "default" — renders only the form body; the caller provides the surrounding Card + header (used on /contact, where the page wraps the form in a white Card with a "Send a Message" heading).
   */
  variant?: ConsultationFormVariant
  /** Override the submit button label. Defaults to "Request a Free Consultation" for hero / "Send Message" for default. */
  submitLabel?: string
  /** Where to redirect after a successful submission. Defaults to /thank-you. */
  redirectTo?: string
}

export function ConsultationForm({
  variant = "default",
  submitLabel,
  redirectTo = "/thank-you",
}: ConsultationFormProps) {
  const router = useRouter()
  const turnstileContainerRef = useRef<HTMLDivElement>(null)
  const turnstileWidgetIdRef = useRef<string | null>(null)
  const [formData, setFormData] = useState({
    "first-name": "",
    "last-name": "",
    email: "",
    phone: "",
    "support-type": "",
    notes: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileError, setTurnstileError] = useState<string | null>(null)

  const updateField =
    (field: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setFormData((prev) => ({ ...prev, [field]: e.target.value }))

  // Render the Turnstile widget (managed mode = visible checkbox) once the
  // api.js script has loaded. Re-renders if the script arrives late.
  useEffect(() => {
    let cancelled = false

    const renderWidget = () => {
      if (cancelled) return
      const container = turnstileContainerRef.current
      const ts = (window as unknown as { turnstile?: { render: (el: HTMLElement, opts: Record<string, unknown>) => string; reset: (id: string) => void } }).turnstile
      if (!container || !ts) return
      // Avoid double-rendering in StrictMode dev.
      if (turnstileWidgetIdRef.current) return

      turnstileWidgetIdRef.current = ts.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "light",
        callback: (token: string) => {
          setTurnstileToken(token)
          setTurnstileError(null)
        },
        "error-callback": () => {
          setTurnstileToken(null)
          setTurnstileError("Verification failed. Please try again.")
        },
        "expired-callback": () => {
          setTurnstileToken(null)
          setTurnstileError("Verification expired. Please check the box again.")
        },
      })
    }

    if (typeof window !== "undefined") {
      const ts = (window as unknown as { turnstile?: { render: typeof renderWidget } }).turnstile
      if (ts) {
        renderWidget()
      } else {
        // Wait for the api.js script to finish loading.
        const interval = window.setInterval(() => {
          if ((window as unknown as { turnstile?: unknown }).turnstile) {
            window.clearInterval(interval)
            renderWidget()
          }
        }, 100)
        // Stop polling after 10s if the script never arrives.
        window.setTimeout(() => window.clearInterval(interval), 10_000)
      }
    }

    return () => {
      cancelled = true
      const id = turnstileWidgetIdRef.current
      const ts = (window as unknown as { turnstile?: { reset: (id: string) => void } }).turnstile
      if (id && ts) {
        try {
          ts.reset(id)
        } catch {
          // Widget may already be gone after unmount; nothing to do.
        }
      }
      turnstileWidgetIdRef.current = null
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!turnstileToken) {
      setTurnstileError("Please complete the verification before submitting.")
      return
    }
    setIsSubmitting(true)
    try {
      const res = await fetch("/api/contact-submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, "cf-turnstile-response": turnstileToken }),
      })
      if (!res.ok) {
        // Try to surface the server's error message (e.g. captcha_failed)
        // so the visitor knows why the submit didn't go through.
        let detail = ""
        try {
          const errBody = await res.json()
          detail = errBody?.error ? `: ${errBody.error}` : ""
        } catch {
          /* body wasn't JSON */
        }
        console.error("[ConsultationForm] submit failed", res.status, detail)
        if (res.status === 400 || res.status === 403) {
          setTurnstileError("Verification failed. Please try again.")
          // Reset the widget so the visitor can re-verify.
          const id = turnstileWidgetIdRef.current
          const ts = (window as unknown as { turnstile?: { reset: (id: string) => void } }).turnstile
          if (id && ts) {
            try {
              ts.reset(id)
            } catch {
              /* ignore */
            }
          }
          setTurnstileToken(null)
          setIsSubmitting(false)
          return
        }
      }
    } catch (err) {
      console.error("[ConsultationForm] submit threw:", err)
    } finally {
      setIsSubmitting(false)
      router.push(redirectTo)
    }
  }

  const isHero = variant === "hero"

  // Hero variant — cream card on a dark band; tall rounded inputs.
  const inputClassName = isHero
    ? "h-12 rounded-xl border-[#e5dfd8] bg-white focus:border-primary/40 focus:ring-primary/20 text-foreground placeholder:text-muted-foreground/50"
    : undefined

  const labelClassName = isHero ? "text-foreground text-sm font-medium" : undefined

  const textareaClassName = isHero
    ? "rounded-xl border-[#e5dfd8] bg-white focus:border-primary/40 focus:ring-primary/20 text-foreground placeholder:text-muted-foreground/50 resize-none"
    : undefined

  const submitClassName = isHero
    ? "w-full h-14 rounded-full bg-primary hover:bg-primary/90 hover:-translate-y-0.5 text-white text-base font-medium shadow-[0_4px_24px_-6px_hsl(270_40%_45%/0.35)] hover:shadow-[0_8px_32px_-8px_hsl(270_40%_45%/0.48)] transition-all duration-300"
    : "w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"

  const defaultSubmitLabel = isHero ? "Request a Free Consultation" : "Send Message"

  const selectClassName = isHero
    ? "w-full h-12 rounded-xl border border-[#e5dfd8] bg-white px-4 text-foreground text-sm focus:outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/20 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2399938b%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px] bg-[right_16px_center] bg-no-repeat"
    : "w-full h-12 rounded-xl border border-input bg-background px-4 text-foreground text-sm focus:outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/20 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2399938b%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px] bg-[right_16px_center] bg-no-repeat"

  const form = (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name row */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="first-name" className={labelClassName}>
            First Name
          </Label>
          <Input
            id="first-name"
            name="first-name"
            type="text"
            value={formData["first-name"]}
            onChange={updateField("first-name")}
            placeholder="Your first name"
            required
            className={inputClassName}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last-name" className={labelClassName}>
            Last Name
          </Label>
          <Input
            id="last-name"
            name="last-name"
            type="text"
            value={formData["last-name"]}
            onChange={updateField("last-name")}
            placeholder="Your last name"
            required
            className={inputClassName}
          />
        </div>
      </div>

      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="email" className={labelClassName}>
          Email Address
        </Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={updateField("email")}
          placeholder="you@example.com"
          required
          className={inputClassName}
        />
      </div>

      {/* Phone */}
      <div className="space-y-2">
        <Label htmlFor="phone" className={labelClassName}>
          Phone Number
        </Label>
        <Input
          id="phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={updateField("phone")}
          placeholder="(555) 123-4567"
          required
          className={inputClassName}
        />
      </div>

      {/* Dropdown */}
      <div className="space-y-2">
        <Label htmlFor="support-type" className={labelClassName}>
          What are you seeking support for?
        </Label>
        <select
          id="support-type"
          name="support-type"
          value={formData["support-type"]}
          onChange={updateField("support-type")}
          required
          className={selectClassName}
        >
          <option value="" disabled>
            Select an area of focus
          </option>
          <option value="anxiety">Anxiety, Stress &amp; Overthinking</option>
          <option value="self-doubt">Self-Doubt &amp; Inner Critic</option>
          <option value="habits">Habits, Cravings &amp; Behavior Change</option>
          <option value="trauma">Trauma &amp; Emotional Healing</option>
          <option value="fears">Fears, Phobias &amp; Panic Attacks</option>
          <option value="focus">Focus, Motivation &amp; ADHD Support</option>
          <option value="relationships">Relationship Challenges</option>
          <option value="addiction">Addiction &amp; Unwanted Habits</option>
          <option value="wellness">Women&rsquo;s Wellness / Other</option>
          <option value="other-personal">Other Personal Issue</option>
        </select>
      </div>

      {/* Textarea */}
      <div className="space-y-2">
        <Label htmlFor="notes" className={labelClassName}>
          Is there anything else you&rsquo;d like Meredith to know?{" "}
          {!isHero && (
            <span className="text-muted-foreground/60 font-normal">(Optional)</span>
          )}
        </Label>
        <Textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={updateField("notes")}
          placeholder={
            isHero
              ? "Share anything that feels important for your first conversation..."
              : "Tell me a little about what you'd like support with..."
          }
          rows={isHero ? 4 : 5}
          className={textareaClassName}
        />
      </div>

      {/* Turnstile widget — managed mode (visible checkbox). Required before submit. */}
      <div className="space-y-2">
        <div ref={turnstileContainerRef} className="cf-turnstile" />
        {turnstileError && (
          <p
            role="alert"
            className="text-sm text-red-600 text-center"
          >
            {turnstileError}
          </p>
        )}
      </div>

      {/* Submit */}
      <Button
        type="submit"
        className={submitClassName}
        disabled={isSubmitting || !turnstileToken}
      >
        {isSubmitting ? "Sending..." : (submitLabel ?? defaultSubmitLabel)}
      </Button>

      <p className="text-xs text-muted-foreground text-center">
        Your information is kept confidential and will never be shared.
      </p>
    </form>
  )

  // Hero variant wraps the form in its own cream Card chrome; default returns just the form so the
  // caller can wrap it (e.g. the /contact page uses a white Card with a "Send a Message" header).
  if (isHero) {
    return (
      <Card className="border-0 bg-[#faf8f5] rounded-[24px] shadow-[0_8px_40px_-12px_hsl(270_25%_10%/0.35)] overflow-hidden">
        <CardContent className="p-8 md:p-10">{form}</CardContent>
      </Card>
    )
  }

  return form
}
